/**
 * ProfileSettingsModal is a custom modal for the ProfileSettingsPage.
 *
 * It renders a portal-based overlay with a styled card containing:
 * - Header: title, subtitle, and close/action icon
 * - Body: form content (children)
 * - Footer: Save button
 *
 * Handles: portal rendering, backdrop click, escape key, scroll disabling.
 */
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { AiOutlineClose } from 'react-icons/ai';

import css from './ProfileSettingsModal.css';

const KEY_CODE_ESCAPE = 27;

// ================ Portal ================ //

class Portal extends React.Component {
  constructor(props) {
    super(props);
    this.el = document.createElement('div');
  }

  componentDidMount() {
    this.props.portalRoot.appendChild(this.el);
  }

  componentWillUnmount() {
    this.props.portalRoot.removeChild(this.el);
  }

  render() {
    return ReactDOM.createPortal(this.props.children, this.el);
  }
}

// ================ Modal ================ //

class ProfileSettingsModal extends Component {
  constructor(props) {
    super(props);
    this.handleBodyKeyUp = this.handleBodyKeyUp.bind(this);
    this.handleBackdropClick = this.handleBackdropClick.bind(this);
    this.containerRef = React.createRef();
    this.state = { portalRoot: null };
  }

  componentDidMount() {
    const { id, isOpen, onManageDisableScrolling } = this.props;
    onManageDisableScrolling(id, isOpen);
    document.body.addEventListener('keyup', this.handleBodyKeyUp);
    this.setState({ portalRoot: document.getElementById('portal-root') });
  }

  componentDidUpdate(prevProps) {
    const { id, isOpen, onManageDisableScrolling } = this.props;
    if (prevProps.isOpen !== isOpen) {
      onManageDisableScrolling(id, isOpen);
      if (isOpen && this.containerRef.current) {
        this.containerRef.current.focus();
      }
    }
  }

  componentWillUnmount() {
    const { id, onManageDisableScrolling } = this.props;
    document.body.removeEventListener('keyup', this.handleBodyKeyUp);
    onManageDisableScrolling(id, false);
  }

  handleBodyKeyUp(event) {
    if (event.keyCode === KEY_CODE_ESCAPE && this.props.isOpen) {
      this.props.onClose();
    }
  }

  handleBackdropClick(event) {
    // Close only when clicking the scroll layer itself, not the card
    if (event.target === event.currentTarget) {
      this.props.onClose();
    }
  }

  render() {
    const {
      isOpen,
      onClose,
      containerClassName,
      title,
      subtitle,
      headerAction,
      onSubmit,
      submitDisabled,
      submitInProgress,
      submitLabel,
      hideFooter,
      children,
    } = this.props;

    const { portalRoot } = this.state;

    if (!portalRoot) return null;

    const overlayClasses = isOpen ? css.overlay : css.hidden;

    const saveButtonClass = submitDisabled ? css.saveButtonDisabled : css.saveButton;

    return (
      <Portal portalRoot={portalRoot}>
        <div className={overlayClasses}>
          <div className={css.scrollLayer} onClick={this.handleBackdropClick}>
            <div
              className={classNames(css.container, containerClassName)}
              ref={this.containerRef}
              tabIndex="-1"
            >
              {/* Header */}
              <div className={css.header}>
                <div className={css.headerText}>
                  <h3 className={css.title}>{title}</h3>
                  {subtitle ? <p className={css.subtitle}>{subtitle}</p> : null}
                </div>
                {headerAction || (
                  <button type="button" className={css.closeButton} onClick={onClose}>
                    <AiOutlineClose className={css.closeIcon} />
                  </button>
                )}
              </div>

              {/* Body */}
              <div className={css.body}>{children}</div>

              {/* Footer */}
              {hideFooter ? null : (
                <div className={css.footer}>
                  <button
                    type="button"
                    className={saveButtonClass}
                    onClick={onSubmit}
                    disabled={submitDisabled || submitInProgress}
                  >
                    {submitLabel || 'Save'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </Portal>
    );
  }
}

ProfileSettingsModal.defaultProps = {
  containerClassName: null,
  subtitle: null,
  headerAction: null,
  submitDisabled: false,
  submitInProgress: false,
  submitLabel: null,
  hideFooter: false,
};

ProfileSettingsModal.propTypes = {
  id: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onManageDisableScrolling: PropTypes.func.isRequired,
  containerClassName: PropTypes.string,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  headerAction: PropTypes.node,
  onSubmit: PropTypes.func.isRequired,
  submitDisabled: PropTypes.bool,
  submitInProgress: PropTypes.bool,
  submitLabel: PropTypes.string,
  hideFooter: PropTypes.bool,
  children: PropTypes.node.isRequired,
};

export default ProfileSettingsModal;
